import { FC, useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { AdaLogo } from "@/components/AdaLogo";

export interface DepositEntry {
  assetId: string;
  amount: string;
}

export interface WalletToken {
  assetId: string;
  name: string;
  balance: string;
  decimals: number;
}

interface AmountStepProps {
  entries: DepositEntry[];
  onEntriesChange: (entries: DepositEntry[]) => void;
  onConfirm: () => void;
  onBack: () => void;
  onSwitchWallet?: () => void;
}

function TokenIcon({ name, className = "w-8 h-8" }: { name: string; className?: string }) {
  if (name === "ADA") return <AdaLogo className={className} />;
  return (
    <div className={`${className} rounded-full bg-sr-surface-raised flex items-center justify-center text-xs font-medium text-sr-pink-muted`}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function TokenSelector({
  selected,
  tokens,
  usedAssetIds,
  onSelect,
}: {
  selected: WalletToken;
  tokens: WalletToken[];
  usedAssetIds: Set<string>;
  onSelect: (assetId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, right: 0 });

  const available = tokens.filter(
    (t) => t.assetId === selected.assetId || !usedAssetIds.has(t.assetId),
  );

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen(!open);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="flex items-center gap-2 px-3 py-2 rounded-full bg-sr-surface border border-white/[0.03] hover:bg-sr-surface-raised transition-colors"
      >
        <TokenIcon name={selected.name} className="w-6 h-6" />
        <span className="text-base font-medium text-white">{selected.name}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-sr-neutral-500">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[61] bg-sr-surface border border-white/[0.03] rounded-xl overflow-y-auto min-w-[160px] max-h-[240px] shadow-xl"
            style={{ top: pos.top, right: pos.right }}
          >
            {available.map((t) => (
              <button
                key={t.assetId}
                onClick={() => { onSelect(t.assetId); setOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2.5 hover:bg-sr-surface-raised transition-colors ${
                  t.assetId === selected.assetId ? "bg-sr-surface-raised" : ""
                }`}
              >
                <TokenIcon name={t.name} className="w-5 h-5" />
                <span className="text-sm text-white">{t.name}</span>
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

export const AmountStep: FC<AmountStepProps> = ({
  entries,
  onEntriesChange,
  onConfirm,
  onBack,
  onSwitchWallet,
}) => {
  const { adaBalance, balance } = useWalletObserver();

  const walletTokens = useMemo<WalletToken[]>(() => {
    const tokens: WalletToken[] = [
      {
        assetId: "ada.lovelace",
        name: "ADA",
        balance: adaBalance.value.toString(),
        decimals: 6,
      },
    ];
    if (balance) {
      for (const [id, amt] of balance) {
        if (id === "ada.lovelace") continue;
        // Skip NFTs (amount = 1 with 0 decimals)
        if (amt.decimals === 0 && amt.amount <= 1n) continue;
        const name = amt.metadata?.name || (() => {
          const hexName = id.split(".")[1] ?? "";
          try { return Buffer.from(hexName, "hex").toString("utf-8"); }
          catch { return hexName.slice(0, 8); }
        })() || "Unknown";
        tokens.push({
          assetId: id,
          name,
          balance: String(amt.value),
          decimals: amt.decimals || 0,
        });
      }
    }
    return tokens;
  }, [adaBalance, balance]);

  const tokenMap = useMemo(
    () => new Map(walletTokens.map((t) => [t.assetId, t])),
    [walletTokens],
  );

  const usedAssetIds = useMemo(
    () => new Set(entries.map((e) => e.assetId)),
    [entries],
  );

  const updateEntry = useCallback(
    (index: number, patch: Partial<DepositEntry>) => {
      const next = entries.map((e, i) => (i === index ? { ...e, ...patch } : e));
      onEntriesChange(next);
    },
    [entries, onEntriesChange],
  );

  const removeEntry = useCallback(
    (index: number) => {
      if (entries.length <= 1) return;
      onEntriesChange(entries.filter((_, i) => i !== index));
    },
    [entries, onEntriesChange],
  );

  const addEntry = useCallback(() => {
    const unused = walletTokens.find((t) => !usedAssetIds.has(t.assetId));
    if (!unused) return;
    onEntriesChange([...entries, { assetId: unused.assetId, amount: "" }]);
  }, [entries, walletTokens, usedAssetIds, onEntriesChange]);

  const hasValidEntry = entries.some((e) => Number(e.amount) > 0);
  const canAddMore = walletTokens.length > entries.length;

  return (
    <>
      {/* Header */}
      <div className="flex flex-col gap-2 items-center text-center">
        <div className="flex items-center gap-3 self-stretch">
          <button
            onClick={onBack}
            className="p-1.5 rounded-full bg-sr-surface border border-white/[0.03] text-sr-neutral-500 hover:text-white transition-colors shrink-0 self-start mt-1"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="text-2xl sm:text-4xl font-medium text-white flex-1">
            What would you like to deposit?
          </h1>
        </div>
        <p className="text-sm sm:text-base text-sr-pink-muted">
          Add one or more tokens to move into your trading balance.
        </p>
      </div>

      {/* Deposit rows */}
      <div className="flex flex-col gap-3 w-full">
        {entries.map((entry, i) => {
          const token = tokenMap.get(entry.assetId);
          const tokenBalance = token?.balance ?? "0";

          return (
            <div key={i} className="sr-card w-full p-4 relative">
              {entries.length > 1 && (
                <button
                  onClick={() => removeEntry(i)}
                  className="absolute top-2 right-2 text-sr-neutral-700 hover:text-sr-salmon transition-colors p-1"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
              <div className="flex items-start justify-between">
                <div className="flex flex-col gap-2 flex-1 min-w-0">
                  <span className="text-xs text-sr-pink-muted tracking-tight">
                    Deposit Amount
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={entry.amount}
                    onChange={(e) => updateEntry(i, { amount: e.target.value })}
                    placeholder="0.00"
                    className="bg-transparent text-[28px] font-medium text-white outline-none w-full min-w-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    style={{ fontFamily: "var(--font-display)" }}
                    autoFocus={i === 0}
                  />
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <TokenSelector
                    selected={token ?? walletTokens[0]}
                    tokens={walletTokens}
                    usedAssetIds={usedAssetIds}
                    onSelect={(assetId) => updateEntry(i, { assetId })}
                  />
                  <div className="text-xs text-sr-pink-muted text-right leading-normal">
                    <span>Available in wallet:</span>
                    <br />
                    <span className="text-sr-bubblegum">
                      {tokenBalance} {token?.name ?? ""}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Add Token */}
        {canAddMore && (
          <button
            onClick={addEntry}
            className="w-full p-3.5 rounded-[24px] bg-sr-pink-glow text-sr-pink-muted text-sm font-medium hover:bg-sr-pink-glow/70 transition-colors"
          >
            Add Token +
          </button>
        )}
      </div>

      {/* CTA */}
      <button
        className="sr-btn-primary"
        disabled={!hasValidEntry}
        onClick={onConfirm}
      >
        Review Deposit
      </button>

      {/* Switch wallet footnote */}
      {onSwitchWallet && (
        <button
          onClick={onSwitchWallet}
          className="text-xs text-sr-neutral-500 hover:text-sr-pink-muted transition-colors"
        >
          Use a different wallet
        </button>
      )}
    </>
  );
};
